/**
 * Storage indicator showing image count and usage.
 */
import React from 'react';
import type { StorageSummary } from '@/types/media';

interface StorageIndicatorProps {
  summary: StorageSummary | undefined;
  isLoading?: boolean;
}

export function StorageIndicator({ summary, isLoading }: StorageIndicatorProps) {
  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-500">
        <div className="w-32 h-2 bg-gray-200 rounded animate-pulse" />
        <span>Loading...</span>
      </div>
    );
  }

  if (!summary) {
    return null;
  }

  const { total_images, max_images } = summary;
  const percentage = (total_images / max_images) * 100;

  // Color based on usage
  let barColor = 'bg-primary-500';
  let textColor = 'text-gray-600';
  if (percentage >= 96) {
    barColor = 'bg-red-500';
    textColor = 'text-red-600';
  } else if (percentage >= 80) {
    barColor = 'bg-yellow-500';
    textColor = 'text-yellow-600';
  }

  return (
    <div className="flex items-center gap-3">
      <div className="w-32">
        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className={`h-full ${barColor} transition-all duration-300`}
            style={{ width: `${Math.min(percentage, 100)}%` }}
          />
        </div>
      </div>
      <span className={`text-sm font-medium ${textColor}`}>
        {total_images}/{max_images} images
      </span>
    </div>
  );
}
